import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';
import { useTeams } from '@/context/TeamContext';
import { api } from '@/lib/api';
import { AttachPlayPicker, PlayAttachmentBadge } from '@/components/PlayAttachment';
import { MessageSquare, Send, Plus, X as XIcon, User as UserIcon } from 'lucide-react';

function fmtTime(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

export default function Notes() {
  const { t } = useLang();
  const { user } = useAuth();
  const { teams, activeTeamId } = useTeams();
  const isCoach = user?.role === 'coach' || user?.role === 'admin';
  const { search } = useLocation();
  const initialTarget = new URLSearchParams(search).get('to');
  const [inbox, setInbox] = useState([]);
  const [selectedId, setSelectedId] = useState(initialTarget || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [attachedPlay, setAttachedPlay] = useState(null);
  const scrollRef = useRef(null);

  // Compose-new-conversation flow:
  //  - Coach: pick a team (or "All my teams") → pick a player → message.
  //    "All my teams" aggregates rosters across every team the coach owns so
  //    they can DM any registered player without hunting for the right team.
  //  - Player: their only valid recipient is their coach (auto-selected).
  const ALL_TEAMS = '__all__';
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTeamId, setComposeTeamId] = useState(ALL_TEAMS);
  const [composeRoster, setComposeRoster] = useState([]);
  const [composeLoading, setComposeLoading] = useState(false);

  const loadInbox = async () => {
    try {
      const r = await api.get('/notes/inbox');
      setInbox(r.data.conversations || []);
      if (!selectedId && r.data.conversations?.[0]) setSelectedId(r.data.conversations[0].other_user_id);
    } catch {}
  };

  const loadConv = async (otherId) => {
    if (!otherId) return;
    try {
      const r = await api.get(`/notes/conversation/${otherId}`);
      setMessages(r.data.messages || []);
      setTimeout(() => scrollRef.current?.scrollTo(0, 99999), 50);
    } catch {}
  };

  useEffect(() => { loadInbox(); }, []);
  useEffect(() => { loadConv(selectedId); }, [selectedId]);

  // Poll the open conversation every 5s so player↔coach replies appear without
  // a manual refresh (mirrors the Groups page pattern).
  useEffect(() => {
    if (!selectedId) return;
    const id = setInterval(() => loadConv(selectedId), 5000);
    return () => clearInterval(id);
  }, [selectedId]);

  const send = async () => {
    if (!text.trim() || !selectedId) return;
    try {
      await api.post('/notes', {
        other_user_id: selectedId,
        message: text,
        play_id: attachedPlay?.id || undefined,
      });
      setText('');
      setAttachedPlay(null);
      loadConv(selectedId);
      loadInbox();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const openCompose = async () => {
    setComposeOpen(true);
    if (isCoach) {
      // Default to aggregated view across every team so a coach can start
      // a note with anyone in 1 tap. They can still narrow down by picking
      // a single team from the dropdown.
      setComposeTeamId(ALL_TEAMS);
      loadComposeRoster(ALL_TEAMS);
    } else {
      // Player: target is always their coach.
      const coachId = user?.coach_id;
      if (coachId) {
        setComposeOpen(false);
        setSelectedId(coachId);
      }
    }
  };

  const loadComposeRoster = async (teamId) => {
    setComposeLoading(true);
    try {
      let players = [];
      if (teamId === ALL_TEAMS) {
        // Aggregate active rosters across every team the coach owns. We
        // dedupe by user_id so a player who happens to be on multiple
        // teams only shows up once in the picker.
        const results = await Promise.all(
          (teams || []).map(tm =>
            api.get(`/roster?team_id=${tm.id}&status=active`).then(r => r.data.players || []).catch(() => [])
          )
        );
        const seen = new Set();
        for (const list of results) {
          for (const p of list) {
            if (!p.user_id || seen.has(p.user_id)) continue;
            seen.add(p.user_id);
            players.push(p);
          }
        }
      } else if (teamId) {
        const r = await api.get(`/roster?team_id=${teamId}&status=active`);
        players = (r.data.players || []).filter(p => p.user_id);
      }
      setComposeRoster(players);
    } catch {
      setComposeRoster([]);
    } finally {
      setComposeLoading(false);
    }
  };

  const pickRecipient = (playerUserId) => {
    setComposeOpen(false);
    setSelectedId(playerUserId);
  };

  const selected = inbox.find(c => c.other_user_id === selectedId);
  const [selectedPeerName, setSelectedPeerName] = useState('');

  // When the selected id has no inbox entry yet (deep-link from Roster /
  // PlayerHome or fresh "compose"), fetch the other user's display name.
  useEffect(() => {
    if (!selectedId || selected) return;
    setSelectedPeerName('');
    api.get(`/profile/coach/${selectedId}`).then(r => {
      setSelectedPeerName(r.data?.display_name || '');
    }).catch(() => setSelectedPeerName(''));
  }, [selectedId, selected]);

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_notes')}</div>
          <h1 className="font-h uppercase text-4xl sm:text-5xl">{t('notes_h1')}</h1>
          <p className="text-gray-400 text-sm mt-1">{t('notes_subtitle')}</p>
        </div>
        <button
          onClick={openCompose}
          data-testid="notes-compose-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light"
        >
          <Plus className="w-4 h-4" /> {t('notes_new') || 'Nuevo mensaje'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-5">
        <div className="bg-card-steel border border-gold/30 rounded-lg p-2">
          {inbox.length === 0 && <p className="text-xs text-gray-400 p-4 text-center">{t('notes_empty_inbox')}</p>}
          {inbox.map(c => (
            <button
              key={c.other_user_id}
              onClick={() => setSelectedId(c.other_user_id)}
              data-testid={`notes-conv-${c.other_user_id}`}
              className={`w-full text-left rounded p-3 mb-1 ${selectedId === c.other_user_id ? 'bg-gold/15' : 'hover:bg-gold/5'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-h uppercase tracking-wider text-sm truncate">{c.other_user_name}</div>
                {c.unread > 0 && <span className="text-[10px] bg-alert text-white rounded-full px-1.5 min-w-[18px] text-center">{c.unread}</span>}
              </div>
              <div className="text-xs text-gray-400 truncate mt-1">{c.last_message}</div>
              <div className="text-[10px] text-gray-500 mt-0.5 font-mono">{fmtTime(c.last_at)}</div>
            </button>
          ))}
        </div>

        <div className="bg-card-steel border border-gold/30 rounded-lg flex flex-col" style={{ minHeight: '500px', maxHeight: 'calc(100vh - 240px)' }}>
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-gray-400"><MessageSquare className="w-12 h-12 opacity-40 mr-3" /> {t('notes_select_conv')}</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gold/20">
                <div className="font-h uppercase tracking-wider">{selected?.other_user_name || selectedPeerName || '...'}</div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.map(m => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? 'bg-gold/15 border border-gold/40' : 'bg-navy-deep border border-steel'}`}>
                        <div className="text-sm whitespace-pre-wrap break-words">{m.message}</div>
                        {m.play_summary && <PlayAttachmentBadge summary={m.play_summary} />}
                        <div className="text-[10px] text-gray-500 mt-1 text-right font-mono">{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <p className="text-center text-xs text-gray-500 py-8">{t('notes_no_messages')}</p>}
              </div>
              <form
                className="border-t border-gold/20 p-3 flex gap-2 items-center"
                onSubmit={e => { e.preventDefault(); send(); }}
              >
                <AttachPlayPicker selected={attachedPlay} onSelect={setAttachedPlay} />
                <input
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder={t('notes_message_placeholder')}
                  data-testid="notes-msg-input"
                  className="flex-1 bg-navy-deep border border-steel rounded px-3 py-2 text-sm text-white"
                  maxLength={2000}
                />
                <button type="submit" data-testid="notes-msg-send" disabled={!text.trim()} className="px-4 py-2 rounded bg-gold text-navy hover:bg-gold-light disabled:opacity-50"><Send className="w-4 h-4" /></button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Compose modal — coach picks Team → Player to start a new conversation */}
      {composeOpen && isCoach && (
        <>
          <div className="fixed inset-0 z-[90] bg-black/70" onClick={() => setComposeOpen(false)} data-testid="notes-compose-backdrop" />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[91] bg-navy-deep border-2 border-gold/60 rounded-xl shadow-2xl p-5 w-[92%] max-w-md" data-testid="notes-compose-modal">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gold/20">
              <h3 className="font-h uppercase text-lg text-gold">{t('notes_new') || 'Nuevo mensaje'}</h3>
              <button onClick={() => setComposeOpen(false)} className="text-gold/70 hover:text-gold" aria-label="Cerrar"><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-gold/80">{t('team_choose') || 'Equipo'}</label>
                <select
                  value={composeTeamId}
                  onChange={e => { setComposeTeamId(e.target.value); loadComposeRoster(e.target.value); }}
                  className="w-full mt-1 bg-card-steel border border-steel rounded px-3 py-2 text-sm text-white outline-none focus:border-gold"
                  data-testid="notes-compose-team"
                >
                  <option value={ALL_TEAMS}>{t('notes_all_teams') || 'Todos mis equipos'}</option>
                  {teams.map(tm => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-gold/80">{t('notes_pick_player') || 'Jugador'}</label>
                {composeLoading ? (
                  <div className="text-gray-400 text-sm py-4 text-center">{t('loading')}…</div>
                ) : composeRoster.length === 0 ? (
                  <div className="text-gray-400 text-sm py-3" data-testid="notes-compose-empty">
                    {composeTeamId === ALL_TEAMS
                      ? (t('notes_no_registered_players_any') || 'Aún no tienes jugadores registrados en ningún equipo. Invitalos desde Roster para poder enviarles notas.')
                      : (t('notes_no_registered_players') || 'Este equipo aún no tiene jugadores registrados. Invitalos desde Roster para poder enviarles notas.')}
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto border border-gold/20 rounded-md divide-y divide-gold/10 mt-1">
                    {composeRoster.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => pickRecipient(p.user_id)}
                        data-testid={`notes-compose-player-${p.id}`}
                        className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-gold/10"
                      >
                        {p.photo_url ? (
                          <img src={p.photo_url} alt={p.name} className="w-8 h-8 rounded-full object-cover border border-gold/30" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold text-xs">
                            <UserIcon className="w-4 h-4" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{p.name}</div>
                          {p.number != null && p.number !== '' && (
                            <div className="text-[10px] text-gold/70 font-mono">#{p.number}</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
